import React from "react";
import { cn } from "@/lib/utils";

/**
 * GCBC brand wordmark — uses the official logotype PNG.
 *
 * • Three tones (matches the brand kit deliverable): "teal" (default, for
 *   light backgrounds), "white" (for dark teal / black backgrounds), "black"
 *   (monochrome / print).
 * • Three sizes: sm / md / lg. Width is auto so the image's intrinsic aspect
 *   ratio is preserved.
 * • Optional `badge` renders a small contextual pill (e.g. "Admin") that
 *   automatically inverts when the tone is "white".
 *
 * The brand name "Gift Card Buyers Club" is baked into the artwork, so there
 * is no separate <span> text — accessibility is preserved via the <img alt>.
 */
const LOGO_SRC = {
  teal:  "/assets/logo/gcbc-expanded-teal.png",
  white: "/assets/logo/gcbc-expanded-white.png",
  black: "/assets/logo/gcbc-expanded-black.png",
};

const SIZES = {
  sm: "h-7",      // 28px — sidebars / compact headers
  md: "h-9",      // 36px — main shells
  lg: "h-12",     // 48px — marketing / hero
};

export function Wordmark({ size = "md", tone = "teal", className, badge = null }) {
  const src = LOGO_SRC[tone] || LOGO_SRC.teal;
  return (
    <div className={cn("inline-flex items-center gap-2", className)} data-testid="brand-wordmark" data-tone={tone}>
      <img
        src={src}
        alt="Gift Card Buyers Club"
        className={cn(SIZES[size] || SIZES.md, "w-auto select-none")}
        draggable={false}
      />
      {badge && (
        <span className={cn(
          "text-[10px] uppercase tracking-[0.08em] font-semibold rounded-sm px-1.5 py-0.5 leading-none",
          tone === "white"
            ? "text-white bg-white/15 border border-white/25"
            : "text-brand-700 bg-brand-50 border border-brand-100",
        )}>
          {badge}
        </span>
      )}
    </div>
  );
}

export default Wordmark;
